import { workPendingInteractionFromEvents } from '@/constants/work';

const validId = value => typeof value === 'string' && /^[a-f0-9]{24}$/.test(value);
const active = task => ['queued', 'running', 'awaiting_input'].includes(task?.status);
export function workActivityState(task, events = []) {
  const conversationId = String(task?.conversation?.id || task?.conversation?._id || task?.conversation || task?.conversationId || '');
  const taskId = String(task?.id || '');
  if (!validId(taskId) || !validId(conversationId)) return null;
  const pending = workPendingInteractionFromEvents(events);
  const end = [...events].reverse().find(event => /^session_(start|resume|end)$/.test(event.type));
  let status = task.status;
  // The chat stream advances before its task-summary record is refreshed.
  // Use this run's lifecycle events, while preserving confirmed terminal state
  // and rejecting events from a previous follow-up.
  const sameTurn = !end?.turnId || !task.activeTurnId || end.turnId === task.activeTurnId;
  const runStart = Date.parse(task.startedAt || task.createdAt);
  const eventTime = Date.parse(end?.ts);
  const currentRun = sameTurn && (!Number.isFinite(runStart) || !Number.isFinite(eventTime) || eventTime >= runStart);
  if (active(task) && end && currentRun) {
    if (end.type === 'session_end') status = end.status || 'completed';
    else if (status === 'queued') status = 'running';
  }
  let phase = status === 'queued' ? 'queued' : 'running';
  if (task.deletedAt || status === 'stopped') phase = 'stopped';
  else if (pending.loginRequest || pending.confirmation || pending.question || status === 'awaiting_input') phase = 'attention';
  else if (['failed', 'interrupted', 'budget_exceeded'].includes(status)) phase = 'failed';
  else if (!active({ status })) phase = end?.reason === 'waiting_on_user' ? 'attention' : 'completed';
  const start = [...events].reverse().find(event => ['session_start', 'session_resume'].includes(event.type));
  const startedAt = Date.parse((currentRun && start?.ts) || task.startedAt || task.createdAt);
  return { taskId, conversationId, phase, steps: 0,
    ...(Number.isFinite(startedAt) && startedAt > 0 ? { startedAt: startedAt / 1000 } : {}) };
}

// Keep the selected task stable while it runs. Terminal updates only end an
// existing native display; they never create one for completed history. Replay
// them after launch/foreground to reconcile activities that outlive the app.
export function createWorkActivityTracker(deliver, { reconcileTerminalTasks = false } = {}) {
  let selected = null, last = null, previousTask = null, previousEvents = null;
  const terminalStates = new Map();
  return (state, reconcile = false) => {
    const tasks = state.tasks || {};
    for (const id of terminalStates.keys()) if (!tasks[id]) terminalStates.delete(id);
    for (const task of reconcileTerminalTasks ? Object.values(tasks) : []) {
      const next = workActivityState(task, state.eventsByTask?.[task.id]);
      if (!next || !['completed', 'failed', 'stopped'].includes(next.phase)) {
        terminalStates.delete(task.id);
        continue;
      }
      const signature = JSON.stringify(next);
      if (reconcile || terminalStates.get(task.id) !== signature) deliver(next);
      terminalStates.set(task.id, signature);
      if (selected === task.id) { selected = null; last = null; }
    }
    let task = selected && tasks[selected];
    if (selected && !task) { if (last) deliver({ ...last, phase: 'stopped' }); selected = null; last = null; }
    if (!selected) {
      task = Object.values(tasks).filter(task => active(task) && !task.deletedAt
        && ['queued', 'running', 'attention'].includes(workActivityState(task, state.eventsByTask?.[task.id])?.phase))
        .sort((a, b) => (Date.parse(b.startedAt || b.createdAt) || 0) - (Date.parse(a.startedAt || a.createdAt) || 0))[0];
      selected = task?.id || null;
    }
    if (!task) return;
    const next = workActivityState(task, state.eventsByTask?.[task.id]);
    if (!next) { selected = null; return; }
    const events = state.eventsByTask?.[task.id];
    if (reconcile || task !== previousTask || events !== previousEvents || JSON.stringify(next) !== JSON.stringify(last)) {
      last = next; previousTask = task; previousEvents = events; deliver(next);
    }
    if (['completed', 'failed', 'stopped'].includes(next.phase)) { selected = null; last = null; }
  };
}
